import React, { useEffect, useMemo, useState } from "react";
import { CalendarRange } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { RANGE_PRESETS, detectPreset, presetRange, rangeLabel, daysInRange } from "../lib/dateRange";

/**
 * Phase 175 — From / To range picker with quick presets (This month · Last month · This quarter · Last quarter ·
 * This financial year · Custom). `value` = {from, to} as 'YYYY-MM-DD'; `onChange(range)` fires only with a valid
 * range (both dates set, from <= to) — an in-progress / inverted edit stays local and is flagged in red.
 * Typing a date switches the preset to "Custom range" automatically.
 */
export const DateRangePicker = ({ value, onChange, pace, testIdPrefix = "range", disabled = false }) => {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);

  const preset = useMemo(() => (draft?.from && draft?.to ? detectPreset(draft) : "custom"), [draft]);
  const valid = !!(draft?.from && draft?.to && draft.from <= draft.to);
  const invalid = !!(draft?.from && draft?.to && draft.from > draft.to);
  const days = valid ? daysInRange(draft) : null;

  const setPreset = (key) => {
    if (key === "custom") return;      // keep the current dates — the admin edits them by hand
    const next = presetRange(key);
    setDraft(next);
    onChange(next);
  };
  const setDate = (field, v) => {
    const next = { ...draft, [field]: v };
    setDraft(next);
    if (next.from && next.to && next.from <= next.to) onChange(next);
  };

  return (
    <div className="flex flex-wrap items-end gap-2" data-testid={`${testIdPrefix}-picker`}>
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Period</Label>
        <Select value={preset} onValueChange={setPreset} disabled={disabled}>
          <SelectTrigger className="h-9 w-[180px]" data-testid={`${testIdPrefix}-preset`}>
            <CalendarRange className="h-3.5 w-3.5 mr-1.5 text-muted-foreground shrink-0" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RANGE_PRESETS.map((p) => (
              <SelectItem key={p.key} value={p.key} data-testid={`${testIdPrefix}-preset-${p.key}`}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground" htmlFor={`${testIdPrefix}-from`}>From</Label>
        <Input id={`${testIdPrefix}-from`} type="date" value={draft?.from || ""} disabled={disabled}
          onChange={(e) => setDate("from", e.target.value)} className={`h-9 w-[150px] cursor-pointer ${invalid ? "border-destructive" : ""}`}
          aria-invalid={invalid} data-testid={`${testIdPrefix}-from`} />
      </div>
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground" htmlFor={`${testIdPrefix}-to`}>To</Label>
        <Input id={`${testIdPrefix}-to`} type="date" value={draft?.to || ""} disabled={disabled}
          onChange={(e) => setDate("to", e.target.value)} className={`h-9 w-[150px] cursor-pointer ${invalid ? "border-destructive" : ""}`}
          aria-invalid={invalid} data-testid={`${testIdPrefix}-to`} />
      </div>
      <p className="text-xs text-muted-foreground pb-2 min-w-0" data-testid={`${testIdPrefix}-summary`}>
        {invalid ? (
          <span className="text-destructive">"To" must be on or after "From"</span>
        ) : valid ? (
          <>
            <span className="font-medium text-foreground">{rangeLabel(draft)}</span>
            {days != null && <> · {days} day{days === 1 ? "" : "s"}</>}
            {pace && pace.days != null && <> · day {pace.elapsed} of {pace.days}</>}
          </>
        ) : (
          <span>Pick both dates</span>
        )}
      </p>
    </div>
  );
};
